import React, { useRef, useState } from "react";
import { IPetRegistraionBulkRegRequest } from "../../typings/interfaces/Requests/PetRegistrationBulkRequestData";
import { defaultAppContextData } from "../../services/appData";
import { useContextService } from "../../services";
import { SaveBulkRegistrationForPets } from "../../api/apimethods";
import Loader from '../../components/molecules/Loader';
import Swal from "sweetalert2";

interface UploadedFile {
  name: string;
  date: string;
  file: File;
}

interface UploadProps {
  onUpload: (fileName: string) => void;
  registrationType: any;
}

const showErrorMessage = (errorMessage: string | undefined): void => {
  Swal.fire({
    title: 'Error!',
    text: errorMessage || 'Something went wrong, please try again.',
    icon: 'error',
    confirmButtonText: 'Okay',
  });
};

const showWarningMessage = (warningMessage: string | undefined): void => {
  Swal.fire({
    title: 'Warning!',
    text: warningMessage || 'Something went wrong, please try again.',
    icon: 'warning',
    confirmButtonText: 'Okay',
  });
};

const UploadPetRegistration: React.FC<UploadProps> = ({
  onUpload,
  registrationType,
}) => {
  const { getContextValueFromStorage } = useContextService();
  const [contextData] = useState(() =>
    getContextValueFromStorage(defaultAppContextData)
  );
  const user = contextData.user;
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const arrayBufferToBase64 = (buffer: ArrayBuffer | Uint8Array) => {
    let binary = "";
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const showSuccessMessage = () => {
    Swal.fire({
      title: 'Success!',
      text: 'The file has been uploaded. Please refer to the dashboard for the file status.',
      icon: 'success',
      confirmButtonText: 'Okay',
    });
  };

  const handleFileProcessed = async (
    bulkRegRequest: IPetRegistraionBulkRegRequest
  ) => {
    try {
      setIsLoading(true);
      // Make the API call using the bulkRegRequests array
      const response = await SaveBulkRegistrationForPets(bulkRegRequest);
      if (response.data.data) {
        setIsLoading(false);
        showSuccessMessage();             
        onUpload(bulkRegRequest.uploadedDocument.fileName);
      } else {
        // API call failed
        // const errorMessage = "An error occurred. Please try again.";
        // console.log("API Response:", errorMessage); // Set the error message in state
        setIsLoading(false);
        const errorMessage = response.data.message;
        showErrorMessage(errorMessage); // Set the error message in state
      }
      // Handle the response as needed
      console.log("API Response:", response);
    } catch (error) {
      setIsLoading(false);
      // Handle any errors that occurred during the API call
      showErrorMessage("An error has occurred. Please try again later.");
      console.error("API Error:", error);
    }
  };

  const handleFilesUpload = (files: FileList) => {
    const validExtensions = ['.xls', '.xlsx'];
    const newFiles: UploadedFile[] = Array.from(files)
    .filter((file) => {
      const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      return validExtensions.includes(fileExtension);
    })
    .map((file) => ({
      name: file.name,
      date: new Date().toLocaleDateString(),
      file,
    }));
    
    if (newFiles.length === 0) {
      showWarningMessage("Please upload only .xls or .xlsx files.");
      return;
    }
    setUploadedFiles([]);
    setUploadedFiles((prevUploadedFiles) => [
      // ...prevUploadedFiles,
      ...newFiles,
    ]);
    newFiles.forEach((uploadedFile) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target) {
          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const base64data = arrayBufferToBase64(data);
          const uploadedDocument = {
            fileName: uploadedFile.name,
            file: base64data,
          };
          const fileBulkRegRequests: IPetRegistraionBulkRegRequest[] = [];
          {/*const workbook = read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const excelData: any[][] = utils.sheet_to_json(worksheet, {
            header: 1,
          });
          
          var nonMandatoryColumns: Array<number> = [];

          if (excelData.length <= 1) {
            showWarningMessage("The uploaded Excel file is empty or contains no data.");
            return;
          }

          if (excelData.length > 1) {
             let columns = 0;
            if (registrationType === 1 || registrationType === 5) {
              columns = 23;
              nonMandatoryColumns = [6, 7, 15, 16, 17, 20, 21, 22];
            } else {
              columns = 22;
              nonMandatoryColumns = [6, 7, 14, 15, 16, 19, 20, 21];
            }

           for (let index = 2; index < excelData.length; index++) {

              const row = excelData[index];

              if(row.length === 0 && index === 2)
              {
                showWarningMessage("The uploaded Excel file does not contain data. Please ensure the file has valid data.");
                return;
              }
              if(row.length > 0)
              {
                let vetLicenceNumberValid = row[0];
                let vetLicenceNameValid = row[1];
                let microchipDateValid = row[2];
                let microchipNumberValid = row[3];
                let regTypeValid = row[5] ?? row[4];
                // Check if the row is empty
                if (!vetLicenceNumberValid || !vetLicenceNameValid || !microchipDateValid || !microchipNumberValid || !regTypeValid) {
                  showWarningMessage("The uploaded Excel file contains empty rows. Please ensure the file has valid data.");
                  return;
                }

                if (row.length > columns) {
                  showWarningMessage("Invalid data: too many columns in a row.");
                  return;
                }

                let regType = row[5]?.toLowerCase();
                if(registrationType == 1)
                {
                  regType = row[4]?.toLowerCase();
                }
                if (regType !== AnimalType[registrationType].toLowerCase())  
                {
                  showWarningMessage("Invalid file uploaded for the selected animal type.");
                  return;
                }
              }
            }

            let vetCentreLicence = excelData[2][0];

            for (let index = 2; index < excelData.length; index++) {
              const row = excelData[index];
              if(row.length > 0)
              {
              for (let column = 0; column < columns; column++) {
                var colExists = nonMandatoryColumns.some((c) => c === column);
                if (
                  (excelData[index][column] === "" ||
                    excelData[index][column] === undefined) &&
                  !colExists
                ) {
                  showWarningMessage("Invalid data. Please ensure the file has valid data.");
                  return;
                } else {
                  if (
                    column === 0 &&
                    vetCentreLicence !== excelData[index][column]
                  ) {
                    showErrorMessage(
                      "Vet centre licence should be same for all the records."
                    );
                    return;
                  }
                }
              }
            }
          }

            const seen = new Set();
            for (let index = 2; index < excelData.length; index++) {
              const row = excelData[index];
              if(row.length > 0)
              {
              let duplicateFound = false;
              const value = excelData[index][3];
              if (seen.has(value)) {
                duplicateFound = true; // Duplicate found
                showWarningMessage(
                  "Duplicate microchip number found. Please enter unique microchip numbers and try again."
                );
                return;
              }
              seen.add(value);
            }
          }*/}
            const bulkRequestData: IPetRegistraionBulkRegRequest = {
              userInformation: {
                name: user?.name,
                idType: user?.idType,
                idNo: user?.uen,
                email: user?.email,
                contactNo: user?.contactNo,
                companyName: user?.companyName,
                uen: user?.uen,
                registeredAddress: user?.registeredAddresses?.[0],
              },
              mode: 1,
              vetCentreLicenceNumber:"",
              registrationType: registrationType,
              uploadedDocument: uploadedDocument,
            };
            fileBulkRegRequests.push(bulkRequestData);
          //}

          handleFileProcessed(fileBulkRegRequests[0]);
        }
      };

      reader.readAsArrayBuffer(uploadedFile.file);
    });
  };

  const handleUploadButtonClick = () => {
    if (
      registrationType === undefined ||
      registrationType === null ||
      registrationType === 0
    ) {
      showWarningMessage("Please select animal type");
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      handleFilesUpload(files);
    }
    event.target.files = null;
  };

  return (
    <>
      <div>
        <button
          type="button"
          className="btn btn-success mt-4"
          onClick={handleUploadButtonClick}
          disabled={isLoading}
        >
           {isLoading ? 'Uploading...' : 'Upload'}
        </button>
        <input
          type="file"
          ref={fileInputRef}
          className="csp-display-none"
          onChange={handleFileChange}
          multiple
          disabled={isLoading}
        />
        {isLoading && (<Loader />)} 
      </div>
    </>
  );
};

export default UploadPetRegistration;
